import { FC } from 'react'
import Footer from '../../components/footer/Footer'
import Menu from '../../components/menu/menu'
import classes from './privacyPolicy.module.scss'

const PrivacyPolicy: FC = () => {
    return (
        <div className={classes.module}>
            <div className={classes.header}>
                <Menu />
            </div>

            <div className={classes.content}>
                <div className={classes.title}>
                    <h1>Privacy Policy</h1>
                    <img src="/images/triangle.svg" alt="divider" />
                </div>

                <div className="w-100 pb-15">

                    {/* 1. Introduction */}
                    <h3 className="fs-2 fw-black mb-4">1. Introduction</h3>
                    <p className="fs-3">
                        Implant Genius™ (“Implant Genius,” “we,” “us,” or “our”) is committed to protecting
                        the privacy, confidentiality, and security of personal information entrusted to us.
                        This Privacy Policy explains how we collect, use, disclose, retain, and protect
                        personal information when you access or use our websites, mobile applications,
                        platforms, and related services (collectively, the “Services”).
                    </p>
                    <p className="fs-3">
                        This Privacy Policy applies to users in the United States and Canada, including
                        patients, dental professionals, clinics, laboratories, and website visitors.
                        It is designed to comply with CCPA/CPRA, HIPAA (where applicable), and PIPEDA.
                    </p>
                    <p className="fs-3">
                        By using our Services, you acknowledge that you have read and understood this
                        Privacy Policy.
                    </p>

                    {/* 2. Our Role */}
                    <h3 className="fs-2 fw-black mb-4">2. Our Role</h3>
                    <p className="fs-3">
                        Implant Genius™ operates as a technology service provider. Dental providers are
                        the independent data controllers of patient medical and dental records.
                    </p>
                    <p className="fs-3">
                        Implant Genius™ acts as a service provider / data processor and processes personal
                        information solely on documented instructions from dental providers.
                    </p>
                    <p className="fs-3">
                        Where applicable, Implant Genius™ acts as a Business Associate under HIPAA and
                        processes protected health information only pursuant to agreements with covered
                        healthcare providers.
                    </p>

                    {/* 3. Information We Collect */}
                    <h3 className="fs-2 fw-black mb-4">3. Information We Collect</h3>
                    <h4 className="fs-3 fw-bold mb-2">3.1 Categories of Personal Information</h4>
                    <ul className="fs-3">
                        <li>Identifiers (name, email, phone number, account credentials)</li>
                        <li>Professional credentials and account identifiers</li>
                        <li>Patient and health information, including dental records and treatment data</li>
                        <li>Facial images, scans, and biological characteristics used solely for diagnosis and treatment planning</li>
                        <li>Technical and usage information (IP address, browser, log data)</li>
                        <li>Limited geolocation derived from IP address (city, region, country only)</li>
                    </ul>
                    <p className="fs-3">
                        We collect only the minimum information necessary to deliver healthcare-related
                        services and meet legal obligations.
                    </p>

                    {/* 4. How We Collect Information */}
                    <h3 className="fs-2 fw-black mb-4">4. How We Collect Information</h3>
                    <ul className="fs-3">
                        <li>Provider and user account registration</li>
                        <li>Secure uploads by licensed dental professionals</li>
                        <li>Use of Implant Genius™ or TX-Genius® mobile applications</li>
                        <li>Website forms and authenticated portals</li>
                        <li>Direct communications</li>
                    </ul>

                    {/* 5. How We Use Personal Information */}
                    <h3 className="fs-2 fw-black mb-4">5. How We Use Personal Information</h3>
                    <ul className="fs-3">
                        <li>Dental diagnosis, treatment planning, and clinical support</li>
                        <li>Secure collaboration between providers, laboratories, and technicians</li>
                        <li>Platform operation, maintenance, and security</li>
                        <li>Account administration and customer support</li>
                        <li>Legal, regulatory, and audit compliance</li>
                        <li>System improvement using aggregated or de-identified data</li>
                    </ul>
                    <p className="fs-3 fw-bold">
                        Implant Genius™ does not sell, rent, commercialize, or use personal information
                        for advertising, marketing, or profiling.
                    </p>

                    {/* 6. Artificial Intelligence */}
                    <h3 className="fs-2 fw-black mb-4">6. Artificial Intelligence and Data Use</h3>
                    <ul className="fs-3">
                        <li>AI technologies support healthcare-related functionality</li>
                        <li>Clinical data may be used to improve system performance in accordance with law</li>
                        <li>Data is aggregated or de-identified where reasonably possible</li>
                        <li>No AI use for advertising, marketing, or resale</li>
                    </ul>

                    {/* 7. Information Sharing */}
                    <h3 className="fs-2 fw-black mb-4">7. Information Sharing and Disclosure</h3>
                    <h4 className="fs-3 fw-bold">7.1 No Sale or Advertising Sharing</h4>
                    <p className="fs-3">
                        Implant Genius™ does not sell personal information and does not share personal
                        information for advertising purposes under CCPA/CPRA.
                    </p>

                    <h4 className="fs-3 fw-bold">7.2 Permitted Disclosures</h4>
                    <ul className="fs-3">
                        <li>Authorized dental providers and healthcare professionals</li>
                        <li>Dental laboratories and technicians involved in care delivery</li>
                        <li>Cloud, security, and IT service providers acting as service providers</li>
                        <li>Courts, regulators, or government authorities when required by law</li>
                    </ul>

                    {/* 8. Data Retention */}
                    <h3 className="fs-2 fw-black mb-4">8. Data Retention</h3>
                    <p className="fs-3">
                        Personal information is retained only as long as necessary to provide services
                        and meet medical, legal, and regulatory requirements. Verified deletion requests
                        are honored unless retention is legally required.
                    </p>

                    {/* 9. Security */}
                    <h3 className="fs-2 fw-black mb-4">9. Security Safeguards</h3>
                    <ul className="fs-3">
                        <li>Encryption of data in transit and at rest</li>
                        <li>Role-based access controls</li>
                        <li>Secure authentication mechanisms</li>
                        <li>Network monitoring and firewalls</li>
                        <li>Periodic security reviews and audits</li>
                    </ul>

                    {/* 10. Privacy Rights */}
                    <h3 className="fs-2 fw-black mb-4">10. Your Privacy Rights</h3>

                    <h4 className="fs-3 fw-bold">10.1 California Residents (CCPA / CPRA)</h4>
                    <ul className="fs-3">
                        <li>Right to Know</li>
                        <li>Right to Delete</li>
                        <li>Right to Correct</li>
                        <li>Right to Limit Use of Sensitive Personal Information</li>
                        <li>Right to Non-Discrimination</li>
                        <li>Authorized Agent rights</li>
                    </ul>

                    <h4 className="fs-3 fw-bold">10.2 Canadian Residents (PIPEDA)</h4>
                    <ul className="fs-3">
                        <li>Right to access personal information</li>
                        <li>Right to request corrections</li>
                        <li>Right to withdraw consent subject to legal limits</li>
                    </ul>

                    {/* 11. Children */}
                    <h3 className="fs-2 fw-black mb-4">11. Children’s Privacy</h3>
                    <p className="fs-3">
                        Implant Genius™ does not knowingly collect personal information directly from
                        children under the age of 13.
                    </p>

                    {/* 12. International Transfers */}
                    <h3 className="fs-2 fw-black mb-4">12. International Data Transfers</h3>
                    <p className="fs-3">
                        Personal information may be processed or stored in the United States or Canada
                        with appropriate safeguards.
                    </p>

                    {/* 13. Third-Party Links */}
                    <h3 className="fs-2 fw-black mb-4">13. Links to Third-Party Sites</h3>
                    <p className="fs-3">
                        Implant Genius™ is not responsible for third-party privacy practices.
                    </p>

                    {/* 14. Policy Changes */}
                    <h3 className="fs-2 fw-black mb-4">14. Changes to This Privacy Policy</h3>
                    <p className="fs-3">
                        Material changes will be posted or communicated as required by law.
                    </p>

                    {/* 15. Contact */}
                    <h3 className="fs-2 fw-black mb-4">15. Contact Us</h3>
                    <p className="fs-3">
                        Implant Genius™<br />
                        3823 Henning Drive, Unit 113<br />
                        Burnaby, BC, V5C 6P3, Canada<br />
                        Phone: +1 604-630-8811<br />
                        Email:{' '}
                        <a href="mailto:info@implantgenius.com" className="text-primary">
                            info@implantgenius.com
                        </a><br />
                        Website:{' '}
                        <a href="https://www.tx-genius.com" target="_blank" rel="noreferrer">
                            https://www.tx-genius.com
                        </a>
                    </p>

                </div>
            </div>

            <Footer />
        </div>
    )
}

export default PrivacyPolicy
